/* eslint-disable @typescript-eslint/no-explicit-any */
import { forkJoin, from, Observable, of, pipe } from 'rxjs';
import { filter, map, startWith, switchMap } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

import { AuthUser } from '@app/account/account-api.model';
import { OrgBrandingLayoutService } from '@app/orgs/services';
import { OrgHelpMenuService } from '@app/orgs/services/org-help-menu.service';
import {
  AuthService,
  ContextService,
  LDFlagsService,
  NavigationService,
  TargetsService,
  TrackerService,
  WebEnvironmentService,
} from '@app/shared/services';
import { TeamFlagsService } from '@app/team/services/team-flags.service';

import { LayoutBrand, LayoutConfiguration } from '@degreed/apollo-angular';
import * as DegreedLayout from './layout-configuration.json';

import {
  addAnalyticTrackers,
  NavOptions,
  updateLxpAdminNavigation, updateHelpMenu, updatei18n,
  updateLxpLearnerNavigation, updateNavigationBranding,
  updateUrls, updateUser, updateVisibility, updateNotificationMenuItem
} from './layout-configuration.utils';
import { PermissionFactory, resolveOrgInfo } from './permission.factory';
import { NotificationService } from '@app/notifications/notification.service';


/**
 * LayoutConfiguration Service
 * 
 * This Angular version (layout-configuration.service.ts) uses RxJS and streams
 * The React version (layout-configuration.hook.ts) does NOT use RxJS 
 * 
 * Note: The `layout-configuration.utils.ts` is shared between the Angular and React versions
 * 
 */


/**
 * Private internal cache of the layout configuration
 */
let layout$: Observable<LayoutConfiguration>;

/**
 * Return a LayoutConfiguration object based on the user's default organization
 * NOTE: Currently we use compiled configuration files: embedded JSON that is runtime transformed.
 *       !! This configuration should be constructed on the server
 *
 * @returns Observable<LayoutConfiguration>
 */
export const buildLayoutConfiguration_v3 = (
  auth: AuthService,
  translate: TranslateService,
  environment: WebEnvironmentService,
  targets: TargetsService,
  navigation: NavigationService,
  featureFlags: LDFlagsService,
  context: ContextService,
  teamFlags: TeamFlagsService,
  helpMenu: OrgHelpMenuService,
  trackerService: TrackerService,
  brandingService: OrgBrandingLayoutService,
  notificationService:  NotificationService,
): Observable<LayoutConfiguration> => {
  
  /**
   * Publish minimal layout configuration WITH cached branding (if available)
   * This allows the Navigation to fast show the branding colors and logo
   */
  const initApolloSkeleton = (): LayoutConfiguration => {
    const brand = brandingService.state.navigation as LayoutBrand;  // already loaded during app initialization
    const aspect = { brand, navigation: { top: [] }, features: { search: {}} }
    return ({ admin: aspect, learner: aspect }) as LayoutConfiguration;
  }

  // Customize the configuration JSON based on the user's organization, i18n and other information
  const buildLayout = pipe(
    filter((user: AuthUser | undefined) => !!user),
    switchMap((user: AuthUser | undefined) => {
      // Gather async data before customizing layout
      const organizationId = resolveOrgInfo(user)[2].organizationId;

      // load org data or defaults if no org
      const supportInfo$ =  organizationId ? helpMenu.getSupportInfo(organizationId) : of(null);
      const learnInSSOUrl$ = organizationId ? navigation.getLearnInSSOUrl(organizationId): of(null);
      const featuredPlanId$ = organizationId ? targets.getBrowseTarget(organizationId).pipe(map((target) => target.targetId)): of(null);;
      const branding$ = organizationId ? from(brandingService.loadBranding(true, organizationId)): of(null);
      const notificationCounts$ =  notificationService.loadShallowCounts();

      return forkJoin([ of(user), supportInfo$, featuredPlanId$, learnInSSOUrl$, branding$, notificationCounts$ ]);
    }),
    map(([ authUser, supportInfo, featuredPlanId, learnInSSOUrl, branding, notificationCounts  ]): LayoutConfiguration => {

      const analyticsUrl = '/analytics?tag=1';
      const [,, orgInfo] = resolveOrgInfo(authUser);
      const factory = new PermissionFactory(authUser, featureFlags, context, environment);
      const navOptions = {isProduction: environment.isProduction, authUser} as NavOptions;

      let configuration = structuredClone({...DegreedLayout}) as LayoutConfiguration;

      // Update i18n asap to get any placeholder variables used in translations updated 
      // in the JSON before any subsequent utils functions are called
      
      configuration = updateVisibility(configuration) as LayoutConfiguration;
    
      configuration = updatei18n(configuration, translate);
      configuration = updateUser(configuration, authUser, environment);
      configuration = updateUrls( configuration, authUser, featuredPlanId, learnInSSOUrl, analyticsUrl );

      configuration = updateLxpAdminNavigation(configuration, factory.forLxpAdmin(), navOptions); 
      configuration = updateLxpLearnerNavigation( configuration, factory.forLxpLearner(), navOptions); 
      configuration = updateNavigationBranding(configuration, branding, translate);

      configuration = updateHelpMenu(configuration, orgInfo.organizationId, supportInfo);
      configuration = addAnalyticTrackers(configuration, trackerService);
      configuration = updateNotificationMenuItem(configuration, notificationCounts);

      return configuration;
    }),
    startWith(initApolloSkeleton()),
  );

  layout$ ||= auth.authUser$.pipe(buildLayout);

  return layout$;
};
